
import { useIsMobile } from "@/hooks/use-mobile";
import { toast } from "@/hooks/use-toast";
import { useEffect, useMemo, useState } from "react";
import { Control, Controller, Path, PathValue, UseFormSetValue } from "react-hook-form";
import Select from "react-select";
import { Teacher, useTeacherSearch } from "../../../hooks/useTeacherSearch";
import FieldWrapper from "../fields/FieldWrapper";
import { customStyles } from "../SelectStyles";

interface TeacherSearchFieldProps<TFieldValues extends Record<string, any>> {
  control: Control<TFieldValues>;
  setValue: UseFormSetValue<TFieldValues>;
  name: Path<TFieldValues>;
  targetField?: Path<TFieldValues>;
  teacherCode?: string;
  disabled?: boolean;
}

export const TeacherSearchField = <TFieldValues extends Record<string, any>>({
  control,
  setValue,
  name,
  targetField,
  teacherCode,
  disabled = false
}: TeacherSearchFieldProps<TFieldValues>) => {
  const [selectedTeacher, setSelectedTeacher] = useState<Teacher | null>(null);
  const isMobile = useIsMobile();

  const {
    searchQuery,
    setSearchQuery,
    teachers,
    isLoading
  } = useTeacherSearch(teacherCode);

  // Handle teacher selection
  const handleSelectTeacher = (option: any) => {
    if (!option) {
      handleClearSelection();
      return;
    }

    const teacher: Teacher = {
      id: option.value,
      code: option.value,
      name: option.label,
      label: option.label
    };
    
    setSelectedTeacher(teacher);
    // Use as PathValue for type safety
    setValue(name, teacher.label as unknown as PathValue<TFieldValues, Path<TFieldValues>>);

    if (targetField) {
      setValue(targetField, teacher.id as unknown as PathValue<TFieldValues, Path<TFieldValues>>);
    }

    toast({
      title: "ครูที่เลือก",
      description: `${teacher.label}`,
    });
  };

  // Handle clearing selection
  const handleClearSelection = () => {
    setSelectedTeacher(null);
    setSearchQuery("");
    setValue(name, "" as unknown as PathValue<TFieldValues, Path<TFieldValues>>);

    if (targetField) {
      setValue(targetField, "" as unknown as PathValue<TFieldValues, Path<TFieldValues>>);
    }
  };

  // Convert teachers array to options format for react-select
  const options = useMemo(() => teachers.map(teacher => ({
    value: teacher.id,
    label: teacher.label
  })), [teachers]);

  const handleInputChange = (inputValue: string) => {
    setSearchQuery(inputValue);
  };

  useEffect(() => {
    if (teacherCode) {
      const teacher = teachers.find(t => t.id === teacherCode);
      if (teacher) {
        setSelectedTeacher(teacher);
      }
    }
  }, [teacherCode, teachers]);

  return (
    <FieldWrapper element={{ id: name, name, type: "select", label: "ค้นหาครู", required: true, description: "ค้นหาด้วย รหัส หรือ ชื่อครู" }}>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Select
            inputValue={searchQuery}
            value={selectedTeacher ? { value: selectedTeacher.id, label: selectedTeacher.label } : null}
            onChange={handleSelectTeacher}
            onInputChange={handleInputChange}
            options={options}
            isLoading={isLoading}
            isDisabled={disabled}
            isClearable={true}
            placeholder="ค้นหารหัสครูชื่อ..."
            noOptionsMessage={() => searchQuery.length < 3 ? "พิมพ์อย่างน้อย 3 ตัวอักษรเพื่อค้นหา" : "ไม่พบข้อมูลครู"}
            styles={customStyles}
            className="react-select-container"
            classNamePrefix="react-select"
            menuPlacement={isMobile ? "auto" : "bottom"}
            menuPosition="fixed"
            ref={field.ref}
            name={field.name}
            onBlur={field.onBlur}
          />
        )}
      />
    </FieldWrapper>
  );
};

export default TeacherSearchField;
